import React, { useState, useEffect, useRef } from "react";
import ApprovalWarrantyPanelComponent from "./approvalWarrantyPanal.component";
import { useDispatch, useSelector } from "react-redux";
import { updateWarrantyLog, verifyWarranty } from "modules/warranty/redux";
import { hideBackdrop, showBackdrop } from "modules/backdrop";
import { useHistory } from "react-router";
import {AccessRights} from "lib/constants/accessRights";
import ApproveRegistrationDialog from "../../dialog/approveRegistrationDialog";
import RejectRegistrationDialog from "../../dialog/rejectRegistrationDialog";
import VoidWarrantyDialog from "../../dialog/voidWarrantyDialog";
import ReinstateWarrantyDialog from "../../dialog/reinstateWarrantyDialog";
import { canAccess } from "../../../../profile/redux/selector/profile.selector";
import EditWarrantyDialog from "../../dialog/editWarrantyDialog";

function ApprovalWarrantyPanelContainer({ pageSetting, language }) {
  const dispatch = useDispatch();
  const canEditWarrantee = useSelector(state =>
    canAccess(state, AccessRights.Warranty.WARRANTEE_LIST_WRITE_UPDATE)
  );

  const canEditWarranteeStatus = useSelector(state =>
    canAccess(state, AccessRights.Warranty.WARRANTEE_LIST_WRITE_UPDATE_STATUS)
  );

  const canEditWarranteeRegistrationStatus = useSelector(state =>
    canAccess(state, AccessRights.Warranty.WARRANTEE_LIST_WRITE_UPDATE_REGISTRATION_STATUS)
  );

  const consumerWarrantyDetail = useSelector(
    (state) => state.warranty.consumerWarrantyDetail
  );

  const warrantySetting = useSelector(
    (state) => state.warranty.warrantySetting
  );

  const warrantyRegisterForm = useSelector(
    (state) => state.warranty.warrantyRegisterForm
  );
  const lang = useSelector((state) => state.constant.languages);

  const [currentActionDialog, setCurrentActionDialog] = useState(null);
  const history = useHistory();

  // Add isMounted ref to track component mount status
  const isMounted = useRef(true);

  // Set up cleanup on unmount
  useEffect(() => {
    return () => {
      // Before unmounting, ensure backdrop is hidden to prevent infinite loading
      dispatch(hideBackdrop());
      isMounted.current = false;
    };
  }, [dispatch]);

  const handleView = async (value) => {
    window.open(value, "_blank");
  };

  const onCloseActionDialogClick = () => {
    setCurrentActionDialog(null);
  };

  const onApproveConfirmClick = async () => {
    try {
      const info = {
        id: consumerWarrantyDetail.id,
        status: "approved",
        reason: "",
        pageSetting: {
          ...pageSetting,
          id: history.location.pathname.substring(
            history.location.pathname.lastIndexOf("/") + 1
          ),
        },
      };
      await dispatch(showBackdrop());
      await dispatch(verifyWarranty(info));
    } finally {
      // Always hide backdrop regardless of component mount status
      dispatch(hideBackdrop());

      // Only update state if component is still mounted
      if (isMounted.current) {
        setCurrentActionDialog(null);
      }
    }
  };

  const onRejectSubmitClick = async (value) => {
    try {
      const info = {
        id: consumerWarrantyDetail.id,
        status: "rejected",
        reason:
          value.otherReason.length === 0 ? value.selected : value.otherReason,
        pageSetting: {
          ...pageSetting,
          id: history.location.pathname.substring(
            history.location.pathname.lastIndexOf("/") + 1
          ),
        },
      };
      await dispatch(showBackdrop());
      await dispatch(verifyWarranty(info));
    } finally {
      // Always hide backdrop regardless of component mount status
      dispatch(hideBackdrop());

      // Only update state if component is still mounted
      if (isMounted.current) {
        setCurrentActionDialog(null);
      }
    }
  };

  const onVoidSubmitClick = async (value) => {
    if (canEditWarrantee) {
      try {
        const info = {
          pageSetting: pageSetting,
          enc: consumerWarrantyDetail.enc,
          action: "void",
          remarks:
            value.otherReason.length === 0 ? value.selected : value.otherReason,
        };

        await dispatch(showBackdrop());
        await dispatch(updateWarrantyLog(info));
      } finally {
        // Always hide backdrop regardless of component mount status
        dispatch(hideBackdrop());

        // Only update state if component is still mounted
        if (isMounted.current) {
          setCurrentActionDialog(null);
        }
      }
    }
  };

  const onReinstateSubmitClick = async (value) => {
    const extendedDate = new Date(consumerWarrantyDetail.endAt).setMonth(
      new Date(consumerWarrantyDetail.endAt).getMonth() + value.extendedMonth
    );

    if (canEditWarrantee) {
      try {
        const info = {
          pageSetting: pageSetting,
          enc: consumerWarrantyDetail.enc,
          action: "reinstate",
          remarks: value.toExtend ? `Extend to ${extendedDate}` : "",
          extended: value.toExtend ? value.extendedMonth : 0,
          extendedTo: extendedDate,
          status: consumerWarrantyDetail.beforeVoidedStatus,
          reason: consumerWarrantyDetail.beforeVoidedReason,
        };

        await dispatch(showBackdrop());
        await dispatch(updateWarrantyLog(info));
      } finally {
        // Always hide backdrop regardless of component mount status
        dispatch(hideBackdrop());

        // Only update state if component is still mounted
        if (isMounted.current) {
          setCurrentActionDialog(null);
        }
      }
    }
  };

  return (
    <>
      <ApprovalWarrantyPanelComponent
        canEditWarrantee={canEditWarrantee}
        canEditWarranteeStatus={canEditWarranteeStatus}
        canEditWarranteeRegistrationStatus={canEditWarranteeRegistrationStatus}
        consumerWarrantyDetail={consumerWarrantyDetail}
        language={language}
        warrantySetting={warrantySetting}
        warrantyRegisterForm={warrantyRegisterForm}
        onApproveClick={() => setCurrentActionDialog("approve")}
        onRejectClick={() => setCurrentActionDialog("reject")}
        onVoidClick={() => setCurrentActionDialog("void")}
        onReinstateClick={() => setCurrentActionDialog("reinstate")}
        onEditClick={() => setCurrentActionDialog("edit")}
        handleView={handleView}
        lang={lang}
      />
      {currentActionDialog === "approve" && (
        <ApproveRegistrationDialog
          isOpen={currentActionDialog === "approve"}
          handleClose={onCloseActionDialogClick}
          handleConfirm={onApproveConfirmClick}
        />
      )}
      {currentActionDialog === "reject" && (
        <RejectRegistrationDialog
          isOpen={currentActionDialog === "reject"}
          handleClose={onCloseActionDialogClick}
          handleSubmit={onRejectSubmitClick}
        />
      )}
      {currentActionDialog === "void" && (
        <VoidWarrantyDialog
          isOpen={currentActionDialog === "void"}
          handleClose={onCloseActionDialogClick}
          handleSubmit={onVoidSubmitClick}
        />
      )}
      {currentActionDialog === "reinstate" && (
        <ReinstateWarrantyDialog
          isOpen={currentActionDialog === "reinstate"}
          handleClose={onCloseActionDialogClick}
          handleSubmit={onReinstateSubmitClick}
          consumerWarrantyDetail={consumerWarrantyDetail}
        />
      )}
      {currentActionDialog === "edit" && (
        <EditWarrantyDialog
          isOpen={currentActionDialog === "edit"}
          handleClose={onCloseActionDialogClick}
        />
      )}
    </>
  );
}

export default ApprovalWarrantyPanelContainer;
